import React, { useState, useEffect } from 'react';
import { organizationAPI } from '../../services/api';
import { Search, Download } from 'lucide-react';

const STATUS_COLORS = {
  captured: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  authorized: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  created: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  refunded: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const fmt = (amount, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);

const fmtDate = (iso) =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const ClientTransactions = ({ organizationId }) => {
  const [transactions, setTransactions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await organizationAPI.getClientTransactions(organizationId);
        setTransactions(res.data.transactions || []);
        setFiltered(res.data.transactions || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load transactions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      transactions.filter(
        (t) =>
          t.client_name?.toLowerCase().includes(q) ||
          t.client_email?.toLowerCase().includes(q) ||
          t.client_contact?.toLowerCase().includes(q) ||
          t.therapist_name?.toLowerCase().includes(q) ||
          t.payment_id?.toLowerCase().includes(q)
      )
    );
  }, [search, transactions]);

  const exportCSV = () => {
    const headers = ['Payment ID', 'Client Name', 'Client Email', 'Client Contact', 'Therapist', 'Amount', 'Currency', 'Status', 'Method', 'Date'];
    const rows = filtered.map((t) => [
      t.payment_id,
      t.client_name,
      t.client_email,
      t.client_contact,
      t.therapist_name,
      t.amount,
      t.currency,
      t.status,
      t.payment_method,
      fmtDate(t.payment_date),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Client Transactions
          </h2>
          <p className="text-sm text-gray-500 dark:text-dark-text-tertiary">
            {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search client, therapist, payment ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text-primary placeholder-gray-400 dark:placeholder-dark-text-tertiary focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72"
            />
          </div>
          <button
            onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-surface border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-border transition-colors"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-dark-text-tertiary">
          {search ? 'No transactions match your search.' : 'No client transactions found.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-dark-surface">
              <tr>
                {['Payment ID', 'Client', 'Therapist', 'Amount', 'Status', 'Method', 'Date'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-tertiary uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg divide-y divide-gray-100 dark:divide-dark-border">
              {filtered.map((t) => (
                <tr key={t.payment_id} className="hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-gray-600 dark:text-dark-text-secondary">
                      {t.payment_id}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      {t.client_name}
                    </div>
                    {t.client_email && (
                      <div className="text-xs text-gray-500 dark:text-dark-text-tertiary">{t.client_email}</div>
                    )}
                    {t.client_contact && (
                      <div className="text-xs text-gray-500 dark:text-dark-text-tertiary">{t.client_contact}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-dark-text-primary">{t.therapist_name}</div>
                    <div className="text-xs text-gray-500 dark:text-dark-text-tertiary">{t.therapist_code}</div>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-dark-text-primary whitespace-nowrap">
                    {fmt(t.amount, t.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        STATUS_COLORS[t.status] || STATUS_COLORS.pending
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary capitalize">
                    {t.payment_method || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary whitespace-nowrap">
                    {fmtDate(t.payment_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientTransactions;
